import React, { useState, useEffect } from "react"
import { injectIntl } from "react-intl"
import { withRouter } from "react-router-dom"
import { Grid, useTheme, useMediaQuery } from "@material-ui/core"
import { Stepper, Button, Box } from "@ridecell/web-components-material"
import { makeStyles } from "@material-ui/core/styles"
import { CreateCustomer } from "../../components/WebAppPortal/AccountCreation"
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"
import ArrowBackIcon from "@material-ui/icons/ArrowBack"
import { Carsharing, Ridecell } from "@ridecell/web-sdk"
import SaveAddress from "../../components/WebAppPortal/Address"
import VerifyCustomer from "../../components/WebAppPortal/VerifyCustomer"
import useGlobalContext from "../../utils/customHooks/useGlobalContext"
import RegistrationSuccess from "../../components/WebAppPortal/RegistrationSuccess"
import PopupModal from "../../utils/elements/PopupModal"
import useCustomer from "../../utils/customHooks/useCustomer"
import LoginDialog from "../../components/LoginDialog"
import IdentityVerification from "../../components/WebAppPortal/IdentityVerification"
import AddPaymentMethod from "../../components/WebAppPortal/PaymentMethod"
import messages from "./messages"
import { getIncompleteRegistrationStep } from "../../utils/common"

const VERIFF = "veriff"

const REGISTRATION_STEP_KEYS = {
  PERSONAL: "personal",
  BUSINESS: "business",
}

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  container: {
    height: "100vh",
    overflowY: "auto",
  },
  hoverable: {
    cursor: "pointer",
  },
})

const getSummaryContent = () => {
  return <RegistrationSuccess />
}

const RegistrationPage = ({ history, intl: { formatMessage }, ...rest }) => {
  const mapStepsToLabels = {
    user_info_step: formatMessage(messages.createAccount),
    verification_step: formatMessage(messages.accountVerification),
    driving_license_step: formatMessage(messages.driverVerification),
    manual_license_correction_step: formatMessage(messages.driverValidation),
    mailing_address_step: formatMessage(messages.address),
    credit_card_step: formatMessage(messages.paymentMethod),
  }
  const defaultTheme = useTheme()
  const isMobile = useMediaQuery(defaultTheme.breakpoints.down("md"))
  const {
    state: {
      tenantId,
      tenantSettings,
      customer,
      isAuthenticated,
      error,
      config,
    },
    toggleBackdrop,
    setError,
    setVeriffSessionData,
    setCurrentUserMarket,
  } = useGlobalContext()
  const [registrationSteps, setRegistrationSteps] = useState(null)

  let defaultSteps = history.location?.state?.defaultSteps
  const { location } = rest
  const query = new URLSearchParams(location.search)
  const creditCardConfirmStep =
    query.get("q") === "confirm_payment" ? "credit_card_step" : null
  const redirectedFrom = query.get("redirectedFrom")
  const [steps, setSteps] = useState(
    defaultSteps || [formatMessage(messages.createAccount), "", "", "", ""]
  )
  const [currentStep, setCurrentStep] = useState(0)
  const { login, logout, fetchCustomer } = useCustomer()
  const [openLoginDialoug, setLoginDialog] = useState(false)
  const classes = useStyles()
  const isLoggedin = isAuthenticated || customer

  let step = history.location?.state?.currentStep || 0
  let selectedProfileMode =
    history.location?.state?.selectedProfileMode ||
    query.get("selectedProfileMode")

  const createVeriffSession = async ({ loggedInCustomer }) => {
    try {
      const registrationStatus = await Ridecell.getRegistrationStatus({
        customerId: loggedInCustomer?.id,
      })
      if (registrationStatus?.details) {
        const customersMarket = await Carsharing.getMarket({
          marketID: registrationStatus?.details?.market_id,
        })
        setCurrentUserMarket(customersMarket)
        const isVeriff = customersMarket
          ? customersMarket.identity_verification_system === VERIFF
          : tenantSettings?.identityVerificationSystemString === VERIFF
        if (isVeriff && customer?.identity_verification_status !== "accepted") {
          const veriffSessionData = await Ridecell.createVeriffSession({
            customerId: loggedInCustomer?.id,
          })
          setVeriffSessionData(veriffSessionData)
        }
      }
    } catch (e) {
      // handle error
    }
  }

  const postCustomerCreation = async (createdCustomer, callback) => {
    let loggedInCustomer = customer || createdCustomer
    try {
      if (!loggedInCustomer?.id) {
        loggedInCustomer = await fetchCustomer()
      }
      toggleBackdrop(true)
      createVeriffSession({ loggedInCustomer })
      const resp = await Ridecell.getRegistrationSteps({
        customerId: loggedInCustomer?.id,
        queryParams: { tenant_id: tenantId },
      })
      const profileMode = loggedInCustomer?.profile_type?.toLowerCase()
      const customerProfileMode =
        selectedProfileMode ||
        (profileMode === REGISTRATION_STEP_KEYS.PERSONAL ? REGISTRATION_STEP_KEYS.PERSONAL : REGISTRATION_STEP_KEYS.BUSINESS)
      const profileSteps = resp[customerProfileMode]
      setRegistrationSteps(profileSteps)
      const temp = []
      Object.keys(mapStepsToLabels).forEach((key) => {
        if (
          profileSteps[key] === true &&
          !["manual_license_correction_step"].includes(key)
        ) {
          // If the payment step enabled in registration criteria
          // but the payments are disabled, do not show the add payment step
          if (
            key === "credit_card_step" &&
            tenantSettings.paymentsEnabled === false
          )
            return
          temp.push(mapStepsToLabels[key])
        }
      })
      setSteps(temp)
    } catch (e) {
      setError(e)
    } finally {
      toggleBackdrop(false)
      if (callback) callback()
    }
  }

  const createCustomer = ({ handleNext }) => {
    return (
      <CreateCustomer
        handleNext={(createdCustomer) =>
          postCustomerCreation(createdCustomer, handleNext)
        }
        createCustomer={null}
      />
    )
  }

  useEffect(() => {
    if ((step || creditCardConfirmStep) && !registrationSteps) {
      postCustomerCreation()
    }
  }, [step])

  const mapCurrentStep = () => {
    let newStep = step || creditCardConfirmStep
    if (newStep && registrationSteps && registrationSteps[newStep]) {
      const currentIndex = steps.findIndex(
        (x) => x === mapStepsToLabels[newStep]
      )
      setCurrentStep(currentIndex)
    }
  }

  useEffect(() => {
    if (registrationSteps) {
      mapCurrentStep()
    }
  }, [registrationSteps, steps])

  useEffect(() => {
    fetchCustomer()
  }, [])

  const getStepContent = ({ activeStep, handleNext }) => {
    let key =
      Object.keys(mapStepsToLabels).find(
        (key) => mapStepsToLabels[key] === steps[activeStep]
      ) || "user_info_step"

    if (isLoggedin && !registrationSteps && key === "user_info_step") {
      // let's not show any user_info_step by default since it has lot of apis which do not needed
      key = null
    }

    const goToNext = async () => {
      if (step || redirectedFrom) {
        // If user comes from incomplete step , then redirecting him
        // to next pending step by checking the multi-purpose registration config
        // Fetch latest customer to get accurate pending step
        const updatedCustomer = await fetchCustomer()
        const pendingStep = getIncompleteRegistrationStep({
          customer: updatedCustomer,
          registrationSteps,
          tenantSettings,
        })
        if (pendingStep) {
          const currentIndex = steps.findIndex(
            (x) => x === mapStepsToLabels[pendingStep]
          )
          if (currentStep !== currentIndex) setCurrentStep(currentIndex)
          else handleNext()
        } else {
          history.push("/")
        }
      } else {
        handleNext()
      }
    }

    switch (key) {
      case "user_info_step":
        return (
          <>
            {tenantSettings.enableCaptcha && (
              <GoogleReCaptchaProvider
                reCaptchaKey={config.captcha_key}
                useEnterprise="true"
              >
                {createCustomer({ handleNext: goToNext })}
              </GoogleReCaptchaProvider>
            )}
            {!tenantSettings.enableCaptcha &&
              createCustomer({ handleNext: goToNext })}
          </>
        )
      case "verification_step":
        return (
          <VerifyCustomer
            history={history}
            currentStep={currentStep}
            defaultSteps={defaultSteps}
            handleNext={goToNext}
          />
        )
      case "driving_license_step" || "manual_license_correction_step":
        return (
          <IdentityVerification
            mivCompleted={
              customer?.identity_verification_status !== "unsubmitted"
            }
            licenseValidationRequired={
              registrationSteps["manual_license_correction_step"]
            }
            handleNext={goToNext}
          />
        )
      case "credit_card_step":
        return (
          <Grid item xs={12} md={12} lg={6}>
            <AddPaymentMethod
              currentStep={currentStep}
              defaultSteps={defaultSteps}
              handleNext={goToNext}
              history={history}
              location={location}
              customerId={customer && customer.id}
              toggleBackdrop={(status) => toggleBackdrop(status)}
              setError={setError}
              headerNeeded={false}
            />
          </Grid>
        )
      case "mailing_address_step":
        return <SaveAddress handleNext={goToNext} />

      default:
        return null
    }
  }

  const displayNextButton = () => {
    return (
      <Button
        color="primary"
        size="large"
        variant="text"
        onClick={isLoggedin ? () => handleLogout() : () => setLoginDialog(true)}
      >
        {isLoggedin
          ? formatMessage(messages.logOut)
          : formatMessage(messages.logIn)}
      </Button>
    )
  }

  const displayBackButton = () => {
    return <ArrowBackIcon className={classes.hoverable} onClick={goToHome} />
  }

  const handleLogin = async (username, password, deviceId) => {
    await login(username, password, deviceId)
    if (!error.hasError) history.push("/")
    setLoginDialog(false)
  }

  const handleLogout = async () => {
    await logout()
    if (!error.hasError) history.push("/login")
  }

  const goToHome = async () => {
    if (isLoggedin) {
      await fetchCustomer()
      history.push("/")
    } else history.push("/login")
  }

  return (
    <Grid container justify="center" xs={12} lg={12}>
      {!isMobile && (
        <Grid style={{ padding: "16px" }} container justify="space-between">
          <Button
            color="primary"
            size="large"
            startIcon={<ArrowBackIcon />}
            variant="text"
            onClick={goToHome}
          >
            {formatMessage(messages.back)}
          </Button>
          <Button
            color="primary"
            size="large"
            variant="text"
            onClick={
              isLoggedin ? () => handleLogout() : () => setLoginDialog(true)
            }
          >
            {isLoggedin
              ? formatMessage(messages.logOut)
              : formatMessage(messages.logIn)}
          </Button>
        </Grid>
      )}
      <Grid
        container
        className={classes.container}
        justify="center"
        lg={8}
        m={5}
      >
        <Box sx={{ marginBottom: "10%", width: "100%" }}>
          <Stepper
            defaultStep={currentStep}
            getStepContent={getStepContent}
            getSteps={() => steps}
            oriantation="horizontal"
            displayNextButton={displayNextButton()}
            displayBackButton={displayBackButton()}
            getSummaryContent={getSummaryContent}
          />
        </Box>
      </Grid>

      <PopupModal
        hasError={error.hasError}
        errorMessage={error.errorMessage}
        onClose={() => setError()}
      />
      <LoginDialog
        handleLogin={handleLogin}
        isMobile={false}
        open={openLoginDialoug}
        history={history}
        handleClose={goToHome}
      />
    </Grid>
  )
}

export default withRouter(injectIntl(RegistrationPage))
